import { useState, useEffect } from 'react';
import { resolvePlayerIdentity, getBlockedActions, type PlayerIdentity } from '@/lib/playerIdentity';
import { useClient } from '@/contexts/ClientContext';

export const usePlayerIdentity = () => {
  const [identity, setIdentity] = useState<PlayerIdentity | null>(null);
  const [blockedActions, setBlockedActions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTester, setIsTester] = useState(false);
  const { client, loading: clientLoading } = useClient();
  
  useEffect(() => {
    const initIdentity = async () => {
      try {
        // Wait for ClientContext to finish loading
        if (clientLoading) {
          setIsLoading(true);
          return;
        }
        
        if (!client?.id) {
          setIsLoading(false);
          return;
        }

        setIsLoading(true);
        
        const playerIdentity = await resolvePlayerIdentity(client.id);
        setIdentity(playerIdentity);
        
        if (playerIdentity) {
          const blocked = await getBlockedActions(playerIdentity.id, client.id);
          setBlockedActions(blocked);
          setIsTester(playerIdentity.is_tester || false);
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('[usePlayerIdentity] Error during init:', error);
        setIsLoading(false);
      }
    };
    
    // Safety timeout: if still loading after 10 seconds, force stop
    const timeoutId = setTimeout(() => {
      if (isLoading) {
        console.warn('[usePlayerIdentity] Timeout reached, forcing isLoading to false');
        setIsLoading(false);
      }
    }, 10000);
    
    initIdentity();
    
    return () => clearTimeout(timeoutId);
  }, [client?.id, clientLoading]);
  
  const refreshBlockedActions = async () => {
    if (identity && client?.id) {
      const blocked = await getBlockedActions(identity.id, client.id);
      setBlockedActions(blocked);
    }
  };
  
  return {
    identity,
    blockedActions,
    isLoading,
    isTester,
    refreshBlockedActions
  };
};
